class DataDate {
  private static readonly YEAR_DAYS = 365;
  private static readonly DAY_SECS = 24 * 60 * 60;
  private static readonly DAY_MSECS = DataDate.DAY_SECS * 1000;
  private static readonly TOO_OLD_DATE = new Date(2000, 0, 1);

  private _date: Date;
  partition: string;

  static getDateForYMD(ymd: string): Date {
    return DataDate.parseYMD(ymd)!.toDate();
  }

  static parseYMD(
    ymd: string | undefined,
    partition: string = "weekly",
  ): DataDate | null {
    if (ymd == null) return null;
    const [y, m, d] = ymd.split("-", 3).map((nums) => parseInt(nums, 10));
    return new DataDate(new Date(y, m - 1, d), partition);
  }

  constructor(date: Date, partition: string) {
    this._date = DataDate.clearTime(date);
    this.partition = partition;
  }

  private static clearTime(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  newDate(y: number, m: number, d: number): DataDate {
    return new DataDate(new Date(y, m, d), this.partition);
  }

  toDate(): Date {
    return this._date;
  }

  unixTime(): number {
    return this._date.getTime();
  }

  year(): number {
    return this._date.getFullYear();
  }

  nextYearStartDate(): DataDate {
    return new DataDate(new Date(this._date.getFullYear() + 1, 0, 1), "weekly");
  }

  month(): number {
    return this._date.getMonth() + 1;
  }

  monthId(): number {
    return this._date.getMonth();
  }

  nextMonthStartDate(): DataDate {
    if (this._date.getMonth() === 11) {
      return new DataDate(
        new Date(this._date.getFullYear() + 1, 0, 1),
        "weekly",
      );
    } else {
      return new DataDate(
        new Date(this._date.getFullYear(), this._date.getMonth() + 1, 1),
        "weekly",
      );
    }
  }

  nextWeek(): DataDate {
    // +0.1 to avoid leap second.
    // DataDate constructor applies clearTime(), 0.1 is going to cleared.
    return new DataDate(DataDate.addDate(this._date, 7.1), this.partition);
  }

  date(): number {
    return this._date.getDate();
  }

  day(): number {
    return this._date.getDay();
  }

  // "2013-05-09"
  ymd(): string {
    return `${this.year()}-${DataDate.zeroFill(this.month(), 2)}-${DataDate.zeroFill(this.date(), 2)}`;
  }

  // "5/17"
  md(): string {
    return `${this.month()}/${this.date()}`;
  }

  y(): string {
    return `${this.year()}年`;
  }

  m(): string {
    return `${this.month()}月`;
  }

  partitionStartDate(): DataDate {
    switch (this.partition) {
      case "daily":
        return this;
      case "weekly":
        return this.weekStartDate();
      case "monthly":
        return this.monthStartDate();
      case "yearly":
        return this.yearStartDate();
      default:
        throw new Error(`must not happen: partition=${this.partition}`);
    }
  }

  // "week" starts from Monday in this application
  weekStartDate(): DataDate {
    const nDiffDays = this.day() === 0 ? 6 : this.day() - 1;
    return this.daysAgo(nDiffDays);
  }

  monthStartDate(): DataDate {
    return this.newDate(this.year(), this.monthId(), 1);
  }

  yearStartDate(): DataDate {
    return this.newDate(this.year(), 0, 1);
  }

  halfYearAgo(): DataDate {
    switch (this.partition) {
      case "daily":
        return this.daysAgo(183);
      case "weekly":
        return this.weeksAgo(26);
      case "monthly":
        return this.sixMonthsAgo();
      default:
        throw new Error(`must not happen: partition=${this.partition}`);
    }
  }

  daysAgo(n: number): DataDate {
    return new DataDate(
      DataDate.clearTime(DataDate.addDate(this._date, -n)),
      this.partition,
    );
  }

  weeksAgo(n: number): DataDate {
    return this.daysAgo(n * 7);
  }

  sixMonthsAgo(): DataDate {
    if (this.monthId() >= 6) {
      return this.newDate(this.year(), this.monthId() - 6, 1);
    } else {
      return this.newDate(this.year() - 1, this.monthId() + 6, 1);
    }
  }

  // N years ago by calendar, N must be integer
  yearsAgo(n: number): DataDate {
    // new Date(2013,1,29) returns Date(2013,2,1), this logic is reasonable
    return this.newDate(this.year() - n, this.monthId(), this.date());
  }

  // N*365 days ago, N can be float
  yearsAgoByDays(n: number): DataDate {
    return this.daysAgo(Math.ceil(n * DataDate.YEAR_DAYS));
  }

  // year-to-date of the date. e.g. yearToDate(Feb 3) -> 34
  yearToDate(origin: Date = DataDate.TOO_OLD_DATE): number {
    return (
      1 +
      Math.ceil((this._date.getTime() - origin.getTime()) / DataDate.DAY_MSECS)
    );
  }

  private static addDate(date: Date, n: number): Date {
    return new Date(date.getTime() + n * DataDate.DAY_MSECS);
  }

  // 2.zeroFill(4)     ->  "0002"
  // 13.zeroFill(4)    ->  "0013"
  // 134.zeroFill(4)   ->  "0134"
  // 2654.zeroFill(4)  ->  "2654"
  // 52654.zeroFill(4) -> "52654"
  private static zeroFill(num: number, width: number): string {
    const s = num.toString();
    return DataDate.replicate("0", width - s.length) + s;
  }

  // "ab".replicate(3) -> "ababab"
  // "ab".replicate(0) -> ""
  private static replicate(str: string, count: number): string {
    if (count < 0) return "";
    return Array(count).fill(str).join("");
  }
}

export { DataDate };
